import * as React from 'react'

import { makeObservable, observable, runInAction } from 'mobx'
import { inject, observer } from 'mobx-react'

import { IPermitTypeField } from '~/client/graph'
import CompanyProfilePreview from '~/client/src/shared/components/CompanyProfilePreview/CompanyProfilePreview'
import StruxhubCompanySelector from '~/client/src/shared/components/StruxhubInputs/StruxhubSelector/StruxhubCompanySelector'
import IPermitFieldsStore from '~/client/src/shared/models/IPermitFieldsStore'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import { NO_VALUE } from '~/client/src/shared/utils/usefulStrings'

import SitePermitCreationFormStore from '../../WorkflowCreationForm.store'
import PermitBaseFormField from './PermitBaseFormField'
import PermitFieldRemoveIcon from './PermitFieldRemoveIcon'

interface IProps {
  typeField: IPermitTypeField
  isViewMode: boolean

  store: SitePermitCreationFormStore
  fieldsStore: IPermitFieldsStore

  tableId?: string
  tableRowIndex?: number
  parentInstanceId?: string
  parentValueIndex?: number

  companiesStore?: CompaniesStore
}

@inject('companiesStore')
@observer
export default class PermitFormCompanyField extends React.Component<IProps> {
  @observable private localProps: IProps = null

  public constructor(props: IProps) {
    super(props)
    this.localProps = props
    makeObservable(this)
  }

  public componentDidUpdate(prevProps: IProps) {
    if (prevProps !== this.props) {
      runInAction(() => {
        this.localProps = this.props
      })
    }
  }

  public render() {
    const {
      isViewMode,
      typeField,
      store,
      fieldsStore,
      parentInstanceId,
      parentValueIndex,
    } = this.props

    return (
      <PermitBaseFormField
        typeField={typeField}
        fieldValues={this.fieldValues}
        isViewMode={isViewMode}
        parentInstanceId={parentInstanceId}
        parentValueIndex={parentValueIndex}
        store={store}
        fieldsStore={fieldsStore}
        componentRenderer={this.renderComponent}
        viewModeRenderer={this.renderViewMode}
        onAddFieldClick={this.addNewFieldValue}
        onRemoveField={this.removeValue}
      />
    )
  }

  private renderComponent = (
    value: string,
    index: number,
    isHeaderShown?: boolean,
  ): JSX.Element => {
    const {
      typeField,
      tableId,
      tableRowIndex,
      fieldsStore,
      companiesStore,
      parentInstanceId,
      parentValueIndex,
    } = this.localProps
    const { caption, isMandatory, isMultiple } = typeField
    const { isFieldChanged } = fieldsStore

    const companyId = !companiesStore.getCompanyById(value) ? '' : value
    const shouldShowRemoveBtn = !!index || !!value

    return (
      <div className="row y-center">
        <StruxhubCompanySelector
          className="overflow-hidden"
          label={caption}
          isRequired={isMandatory}
          isChanged={isFieldChanged(
            typeField.id,
            index,
            tableId,
            tableRowIndex,
            parentInstanceId,
            parentValueIndex,
          )}
          isLabelHidden={!!index && !isHeaderShown}
          isHelperTextCondensed={isMultiple && !isHeaderShown}
          isTopMarginForced={isMultiple && !index}
          isDeactivatedShown
          value={companyId}
          onClick={this.openCompanyPicker.bind(this, index)}
          testId="workflow-company-selector"
        />
        {isMultiple && shouldShowRemoveBtn && !isHeaderShown && (
          <PermitFieldRemoveIcon index={index} onClick={this.removeValue} />
        )}
      </div>
    )
  }

  private renderViewMode = (value: string): JSX.Element => {
    const company = this.localProps.companiesStore.getCompanyById(value)

    if (!company) {
      return <div className="text large word-break-all">{NO_VALUE}</div>
    }

    return (
      <CompanyProfilePreview
        key={company.id}
        company={company}
        isDeactivatedShown
        isMenuHidden
        isPhoneHidden
      />
    )
  }

  private get fieldValues(): string[] {
    const {
      typeField,
      tableId,
      tableRowIndex,
      fieldsStore,
      parentInstanceId,
      parentValueIndex,
    } = this.props

    const values = fieldsStore.getFieldValues<string>(
      typeField.id,
      tableId,
      tableRowIndex,
      false,
      parentInstanceId,
      parentValueIndex,
    )

    return values?.length ? values : ['']
  }

  private openCompanyPicker(fieldIndex: number) {
    const {
      store,
      typeField,
      tableId,
      tableRowIndex,
      parentInstanceId,
      parentValueIndex,
    } = this.props

    store.setSelectedField(
      typeField,
      fieldIndex,
      tableId,
      tableRowIndex,
      parentInstanceId,
      parentValueIndex,
    )
  }

  private addNewFieldValue = () => {
    if (!this.isTableField) {
      const { fieldsStore, typeField, parentInstanceId, parentValueIndex } =
        this.props

      fieldsStore.addNewFieldValue(
        typeField,
        '',
        parentInstanceId,
        parentValueIndex,
      )
    }
  }

  private removeValue = (index: number) => {
    if (!this.isTableField) {
      const { fieldsStore, typeField, parentInstanceId, parentValueIndex } =
        this.props

      fieldsStore.removeFieldValue(
        typeField.id,
        index,
        parentInstanceId,
        parentValueIndex,
      )
    }
  }

  private get isTableField(): boolean {
    return !!this.props.tableId
  }
}
